import type { EditorState, Extension, Range } from '@codemirror/state';
import {
  Decoration,
  EditorView,
  ViewPlugin,
  WidgetType,
  type DecorationSet,
  type ViewUpdate,
} from '@codemirror/view';
import { ensureSyntaxTree, syntaxTree } from '@codemirror/language';
import type { SyntaxNode } from '@lezer/common';

/**
 * Obsidian-style live preview for markdown: formatting renders inline and the
 * syntax marks (##, **, backticks, link/image syntax, task markers) are hidden
 * unless the cursor is on that element — block-level marks reveal per line,
 * inline marks reveal when the selection touches the element.
 */

class CheckboxWidget extends WidgetType {
  constructor(
    readonly checked: boolean,
    readonly pos: number,
  ) {
    super();
  }

  override eq(other: CheckboxWidget): boolean {
    return other.checked === this.checked && other.pos === this.pos;
  }

  override toDOM(view: EditorView): HTMLElement {
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = this.checked;
    input.className = 'cm-lp-task';
    // Keep the editor from treating the click as a text selection.
    input.addEventListener('mousedown', (e) => e.preventDefault());
    input.addEventListener('change', () => {
      view.dispatch({
        changes: {
          from: this.pos,
          to: this.pos + 3,
          insert: this.checked ? '[ ]' : '[x]',
        },
      });
    });
    return input;
  }
}

class ImageWidget extends WidgetType {
  constructor(
    readonly src: string,
    readonly alt: string,
  ) {
    super();
  }

  override eq(other: ImageWidget): boolean {
    return other.src === this.src && other.alt === this.alt;
  }

  override toDOM(): HTMLElement {
    const img = document.createElement('img');
    img.src = this.src;
    img.alt = this.alt;
    img.className = 'cm-lp-image';
    return img;
  }
}

const headingLevel = (name: string): number | null => {
  const m = /^ATXHeading([1-6])$/.exec(name);
  return m ? Number(m[1]) : null;
};

const mark = (cls: string) => Decoration.mark({ class: cls });
const line = (cls: string) => Decoration.line({ class: cls });
const hide = Decoration.replace({});
/** Faint styling for syntax marks while they are revealed. */
const syntaxMark = mark('cm-lp-mark');

export function buildDecorations(state: EditorState): DecorationSet {
  // The language plugin parses lazily; force the (small) brief to parse fully
  // so decorations don't lag behind the visible text.
  const tree = ensureSyntaxTree(state, state.doc.length, 50) ?? syntaxTree(state);
  const { doc, selection } = state;
  const decos: Range<Decoration>[] = [];

  const activeLines = new Set<number>();
  for (const r of selection.ranges) {
    const last = doc.lineAt(r.to).number;
    for (let n = doc.lineAt(r.from).number; n <= last; n++) activeLines.add(n);
  }
  const lineIsActive = (pos: number) => activeLines.has(doc.lineAt(pos).number);
  const selectionTouches = (from: number, to: number) =>
    selection.ranges.some((r) => r.from <= to && r.to >= from);

  const eachLine = (from: number, to: number, deco: Decoration) => {
    const last = doc.lineAt(to).number;
    for (let n = doc.lineAt(from).number; n <= last; n++) {
      decos.push(deco.range(doc.line(n).from));
    }
  };

  /** Hide an inline element's marks, or style them faint while revealed. */
  const inlineMarks = (
    node: SyntaxNode,
    markName: string,
    styleClass: string,
  ) => {
    decos.push(mark(styleClass).range(node.from, node.to));
    const revealed = selectionTouches(node.from, node.to);
    for (const m of node.getChildren(markName)) {
      decos.push((revealed ? syntaxMark : hide).range(m.from, m.to));
    }
  };

  tree.iterate({
    enter: (ref) => {
      const level = headingLevel(ref.name);
      if (level !== null) {
        decos.push(line(`cm-lp-heading cm-lp-h${level}`).range(doc.lineAt(ref.from).from));
        const marker = ref.node.getChild('HeaderMark');
        if (marker) {
          // Swallow the space after the marks too, so the heading text sits flush.
          const to =
            doc.sliceString(marker.to, marker.to + 1) === ' ' ? marker.to + 1 : marker.to;
          decos.push(
            (lineIsActive(ref.from) ? syntaxMark : hide).range(marker.from, to),
          );
        }
        return;
      }

      switch (ref.name) {
        case 'StrongEmphasis':
          inlineMarks(ref.node, 'EmphasisMark', 'cm-lp-strong');
          return;
        case 'Emphasis':
          inlineMarks(ref.node, 'EmphasisMark', 'cm-lp-em');
          return;
        case 'Strikethrough':
          inlineMarks(ref.node, 'StrikethroughMark', 'cm-lp-strike');
          return;
        case 'InlineCode':
          inlineMarks(ref.node, 'CodeMark', 'cm-lp-inline-code');
          return;
        case 'Link': {
          decos.push(mark('cm-lp-link').range(ref.from, ref.to));
          const marks = ref.node.getChildren('LinkMark');
          if (marks.length >= 2) {
            if (selectionTouches(ref.from, ref.to)) {
              decos.push(syntaxMark.range(marks[0].from, marks[0].to));
              decos.push(syntaxMark.range(marks[1].from, ref.to));
            } else {
              decos.push(hide.range(marks[0].from, marks[0].to)); // [
              decos.push(hide.range(marks[1].from, ref.to)); // ](url)
            }
          }
          return false; // children are covered by the ranges above
        }
        case 'Image': {
          if (lineIsActive(ref.from)) return false;
          const url = ref.node.getChild('URL');
          if (!url) return false;
          const marks = ref.node.getChildren('LinkMark');
          const alt =
            marks.length >= 2 ? doc.sliceString(marks[0].to, marks[1].from) : '';
          decos.push(
            Decoration.replace({
              widget: new ImageWidget(doc.sliceString(url.from, url.to), alt),
            }).range(ref.from, ref.to),
          );
          return false;
        }
        case 'TaskMarker': {
          if (lineIsActive(ref.from)) return;
          const checked = /x/i.test(doc.sliceString(ref.from, ref.to));
          decos.push(
            Decoration.replace({
              widget: new CheckboxWidget(checked, ref.from),
            }).range(ref.from, ref.to),
          );
          return;
        }
        case 'ListMark':
          decos.push(mark('cm-lp-list-mark').range(ref.from, ref.to));
          return;
        case 'QuoteMark':
          decos.push(syntaxMark.range(ref.from, ref.to));
          return;
        case 'Blockquote':
          eachLine(ref.from, ref.to, line('cm-lp-quote'));
          return;
        case 'FencedCode': {
          eachLine(ref.from, ref.to, line('cm-lp-code-block'));
          for (const m of ref.node.getChildren('CodeMark')) {
            decos.push(syntaxMark.range(m.from, m.to));
          }
          const info = ref.node.getChild('CodeInfo');
          if (info) decos.push(syntaxMark.range(info.from, info.to));
          return false;
        }
      }
    },
  });

  return Decoration.set(decos, true);
}

const livePreviewPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = buildDecorations(view.state);
    }

    update(update: ViewUpdate) {
      if (
        update.docChanged ||
        update.selectionSet ||
        syntaxTree(update.state) !== syntaxTree(update.startState)
      ) {
        this.decorations = buildDecorations(update.state);
      }
    }
  },
  { decorations: (v) => v.decorations },
);

// Dispatch Desk palette (light-only by design) — values from tailwind.config.js.
const livePreviewTheme = EditorView.baseTheme({
  '.cm-lp-heading': { fontWeight: '600', color: '#212326' },
  '.cm-lp-h1': { fontSize: '1.5em' },
  '.cm-lp-h2': { fontSize: '1.25em' },
  '.cm-lp-h3': { fontSize: '1.1em' },
  '.cm-lp-strong': { fontWeight: '600' },
  '.cm-lp-em': { fontStyle: 'italic' },
  '.cm-lp-strike': { textDecoration: 'line-through', color: '#6B6E71' },
  '.cm-lp-inline-code': {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Consolas, Liberation Mono, monospace',
    fontSize: '0.85em',
    backgroundColor: '#F0EFEB',
    borderRadius: '3px',
    padding: '1px 3px',
  },
  '.cm-lp-link': {
    color: '#E14E0E',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
  '.cm-lp-list-mark': { color: '#8B8E91' },
  '.cm-lp-mark': { color: '#B9B7B0' },
  '.cm-lp-quote': {
    borderLeft: '2px solid #B9B7B0',
    paddingLeft: '8px',
    color: '#6B6E71',
  },
  '.cm-lp-code-block': {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Consolas, Liberation Mono, monospace',
    fontSize: '0.85em',
    backgroundColor: '#F0EFEB',
  },
  '.cm-lp-image': {
    maxWidth: '100%',
    maxHeight: '320px',
    borderRadius: '5px',
    display: 'block',
    margin: '4px 0',
  },
  '.cm-lp-task': {
    accentColor: '#E14E0E',
    cursor: 'pointer',
    marginRight: '2px',
  },
});

export function markdownLivePreview(): Extension {
  return [livePreviewPlugin, livePreviewTheme];
}
